import { v4 as uuidv4 } from "uuid";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import PowerSlidePreview from "../../features/previews/power_slide/PowerSlidePreview";
import PowerSlideThemeEditor from "../../features/previews/power_slide/PowerSlideThemeEditor";
import { stripFormattingTags } from "../../features/rich_text/RichTextHtmlUtils";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { Actor } from "../Actor";
import { Prop } from "../Prop";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";
import {
  collectPowerSlideKeys,
  countPowerSlideTemplateKeys,
  createPowerSlide,
  getPowerSlideFieldKeys,
  getPowerSlideTitleKey,
  IPowerSlide,
  ISlide,
  remapPowerSlide,
  resolvePowerSlideTheme,
} from "./PowerSlideHelper";
import {
  DEFAULT_POWER_SLIDE_TEMPLATE,
  DEFAULT_POWER_SLIDE_TEMPLATE_ID,
  getPowerSlideTemplate,
  POWER_SLIDE_TITLE_FIELD,
} from "./PowerSlideTemplates";
import {
  clonePowerSlideTheme,
  DEFAULT_POWER_SLIDE_THEME,
  normalizePowerSlideTheme,
} from "./PowerSlideTheme";

/** True when any language holds real text for this key. */
function hasLocalizedText(
  key: string,
  records: LocalizedStringRecord | undefined
): boolean {
  if (!key) return false;
  const entry = records?.[key];
  if (!entry) return false;
  return Object.values(entry).some(
    (localized) => stripFormattingTags(localized?.value ?? "").trim() !== ""
  );
}

/** Repairs a slide read from an older or hand-edited training. */
function migratePowerSlideSlide(slide: ISlide): ISlide {
  return {
    ...slide,
    id: slide.id || uuidv4(),
    templateId: slide.templateId ?? DEFAULT_POWER_SLIDE_TEMPLATE,
    fields: slide.fields ?? {},
    assets: slide.assets ?? [],
  };
}

export const powerSlideInteraction = (): InteractionFactoryEntity => {
  const component = <PowerSlidePreview />;

  /**
   * A new deck starts empty and the editor opens the template picker. A
   * PowerSlide is not always a standalone presentation - it may be a three
   * slide aside inside a scene - so guessing a first template is wrong.
   */
  /**
   * A new deck arrives with its first slide already in it. An empty deck is a
   * dead end: it fails validation the moment it is created, and the editor can
   * only offer a button that produces the very slide this makes for free.
   */
  const create = (getKeys: (amount: number) => string[]): IPowerSlide => ({
    id: uuidv4(),
    name: "PowerSlide",
    type: InteractionType.PowerSlide,
    slides: [
      createPowerSlide(
        DEFAULT_POWER_SLIDE_TEMPLATE_ID,
        getKeys(countPowerSlideTemplateKeys(DEFAULT_POWER_SLIDE_TEMPLATE_ID))
      ),
    ],
    theme: DEFAULT_POWER_SLIDE_THEME,
  });

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): IPowerSlide => {
    const deck = interaction as IPowerSlide;
    return {
      ...deck,
      slides: (deck.slides ?? []).map(migratePowerSlideSlide),
      // A deck saved before the theme moved off the training has none, and a
      // hand-edited one may have a broken colour; both land on the default.
      theme: normalizePowerSlideTheme(deck.theme),
    };
  };

  /**
   * Fresh ids for the deck and every slide, fresh localization keys for every
   * field, and no container shared with the source - see
   * docs/InteractionCopyHighConfidencePlan.md for the contract.
   */
  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void
  ): IPowerSlide => {
    const deck = interaction as IPowerSlide;
    const oldKeys = collectPowerSlideKeys(deck);
    const newKeys = getKeys(oldKeys.length);
    const keyMap = new Map<string, string>();
    oldKeys.forEach((oldKey, index) => {
      const newKey = newKeys[index];
      if (!newKey) return;
      keyMap.set(oldKey, newKey);
      replaceKey(oldKey, newKey);
    });

    return {
      ...deck,
      id: uuidv4(),
      name: interaction.name,
      type: InteractionType.PowerSlide,
      slides: (deck.slides ?? []).map((slide) =>
        remapPowerSlide(slide, keyMap)
      ),
      // The copy takes the source's look with it - including into another
      // training - but on its own object, so retheming one cannot touch both.
      theme: clonePowerSlideTheme(resolvePowerSlideTheme(deck)),
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    currentTake: Take,
    actorPresets: Actor[]
  ): string[] => {
    const deck = interaction as IPowerSlide;
    const warnings: string[] = [];

    (deck.slides ?? []).forEach((slide, index) => {
      const template = getPowerSlideTemplate(slide.templateId);
      if (!template) return;
      template.textFields.forEach((field) => {
        // A required field is a blocking error, reported by ValidateErrors.
        if (field.required) return;
        const keys = getPowerSlideFieldKeys(slide, field.id);
        keys.forEach((key, position) =>
          ValidationHelper.ValidateKey(
            key,
            getValue,
            warnings,
            field.repeating
              ? `Slide ${index + 1} ${field.label} ${position + 1}`
              : `Slide ${index + 1} ${field.label}`
          )
        );
      });
    });

    return warnings;
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord
  ): string[] => {
    const deck = interaction as IPowerSlide;
    const errors: string[] = [];

    if ((deck.slides ?? []).length === 0) {
      errors.push("Please add at least one slide");
      return errors;
    }

    deck.slides.forEach((slide, index) => {
      const template = getPowerSlideTemplate(slide.templateId);
      if (!template) {
        errors.push(
          `Slide ${index + 1} uses an unknown template (${slide.templateId})`
        );
        return;
      }
      template.textFields.forEach((field) => {
        const keys = getPowerSlideFieldKeys(slide, field.id);

        if (field.required) {
          const filled = keys.some((key) =>
            hasLocalizedText(key, localizedStringRecords)
          );
          if (!filled) errors.push(`Slide ${index + 1} has no ${field.label}`);
        }

        // A list outside its template's bounds means hand-edited or migrated
        // JSON; the renderer would silently show the wrong number of rows.
        if (!field.repeating) return;
        const min = field.minItems ?? 0;
        const max = field.maxItems ?? Number.MAX_SAFE_INTEGER;
        if (keys.length < min || keys.length > max) {
          errors.push(
            `Slide ${index + 1} must have between ${min} and ${max} ${field.label} entries`
          );
        }

        // Paired lists are parallel, so a length mismatch silently re-pairs
        // every entry after the gap - value 3 captioned by label 4. The editor
        // cannot produce this, but hand-edited or migrated JSON can, and it is
        // invisible in the rendered slide.
        if (!field.pairedWith) return;
        const partnerKeys = getPowerSlideFieldKeys(slide, field.pairedWith);
        if (partnerKeys.length !== keys.length) {
          errors.push(
            `Slide ${index + 1} has ${keys.length} ${field.label} entries but ${partnerKeys.length} paired entries`
          );
        }
      });
    });

    return errors;
  };

  const filterType: string = "PowerSlide";

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[]
  ): SearchResultData[] => {
    const deck = interaction as IPowerSlide;
    const searchableStrings: string[] = [
      deck.name,
      ...collectPowerSlideKeys(deck),
    ];

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType
    );
  };

  return {
    View: component,
    // Theme is a training-level setting rather than a property of this
    // interaction, so the editor reads and writes the training directly.
    PropertiesEditor: {
      render: () => <PowerSlideThemeEditor />,
    },
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};

export type { IPowerSlide, ISlide };
export { POWER_SLIDE_TITLE_FIELD, getPowerSlideTitleKey };
